import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, registerInactiveAccountHandler } from "@/lib/api";

const AuthContext = createContext(null);

// View-switcher constants — surfaced on the navbar dropdown and persisted
// to localStorage so multi-role users keep the same lens across reloads.
// Priority order matches the capability hierarchy: admin > provider > customer.
export const VIEW_PRIORITY = ["admin", "provider", "customer"];
const VIEW_STORAGE_KEY = "pp_last_dashboard";

function readStoredView() {
  try {
    const raw = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (raw && VIEW_PRIORITY.includes(raw)) return raw;
  } catch {
    /* localStorage unavailable */
  }
  return null;
}

function persistView(view) {
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    /* localStorage unavailable */
  }
}

function deriveAvailableViews(user) {
  if (!user) return [];
  const out = [];
  if (user.is_admin) out.push("admin");
  if (user.is_provider) out.push("provider");
  if (user.is_customer) out.push("customer");
  return out;
}

function pickDefaultView(user) {
  const available = deriveAvailableViews(user);
  // Prefer the persisted choice if it's still valid for this user.
  const stored = readStoredView();
  if (stored && available.includes(stored)) return stored;
  // Otherwise pick the highest-priority capability the user has.
  return available[0] || null;
}

export function AuthProvider({ children }) {
  // user state: undefined = loading, null = unauth, object = authed
  const [user, setUser] = useState(undefined);
  // Profile shapes (multi-role): customer + provider may BOTH be set.
  // `profile` is the legacy single-profile alias for old call sites.
  const [profile, setProfile] = useState(null);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [providerProfile, setProviderProfile] = useState(null);
  const [view, setViewState] = useState(null);

  const refreshMe = useCallback(async () => {
    const token = localStorage.getItem("pp_token");
    if (!token) {
      setUser(null);
      setProfile(null);
      setCustomerProfile(null);
      setProviderProfile(null);
      setViewState(null);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setProfile(data.profile);
      setCustomerProfile(data.customer_profile || null);
      setProviderProfile(data.provider_profile || null);
      // Reconcile the active view with the user's current capabilities.
      const nextView = pickDefaultView(data.user);
      if (nextView) {
        setViewState(nextView);
        persistView(nextView);
      } else {
        setViewState(null);
      }
      return data;
    } catch (e) {
      localStorage.removeItem("pp_token");
      setUser(null);
      setProfile(null);
      setCustomerProfile(null);
      setProviderProfile(null);
      setViewState(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  // Phase 3 §3 — bridge the axios 403 "Account inactive" interceptor
  // back into React state. The interceptor calls refreshMe so the
  // lenient /auth/me returns the fresh `status: "suspended"` payload
  // and <ProtectedRoute> takes the user to <InactiveAccountSplash>
  // on the very next render — no manual reload required.
  useEffect(() => {
    registerInactiveAccountHandler(() => {
      refreshMe();
    });
    return () => registerInactiveAccountHandler(null);
  }, [refreshMe]);

  const setSession = (token, u) => {
    localStorage.setItem("pp_token", token);
    setUser(u);
    // Lock in the default view immediately so the post-login redirect
    // doesn't need a refreshMe round-trip first.
    const next = pickDefaultView(u);
    if (next) {
      setViewState(next);
      persistView(next);
    }
  };

  const logout = () => {
    localStorage.removeItem("pp_token");
    setUser(null);
    setProfile(null);
    setCustomerProfile(null);
    setProviderProfile(null);
    setViewState(null);
  };

  // Public setter — caller passes one of "admin" | "provider" | "customer".
  // No-op (with a console warning) if the view isn't available for the
  // current user; UI consumers should pre-filter to the available views.
  const setView = useCallback(
    (next) => {
      if (!user) return;
      const available = deriveAvailableViews(user);
      if (!available.includes(next)) {
        console.warn(`setView("${next}") — not available for current user`);
        return;
      }
      setViewState(next);
      persistView(next);
    },
    [user]
  );

  // Derived helpers — memoized to keep equality cheap for consumers.
  const availableViews = useMemo(() => deriveAvailableViews(user), [user]);
  const hasMultipleViews = availableViews.length > 1;

  // Convenience capability flag predicates exposed to consumers so
  // they don't all repeat `!!user?.is_customer` etc.
  const isAdmin = !!user?.is_admin;
  const isCustomer = !!user?.is_customer;
  const isProvider = !!user?.is_provider;

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        customerProfile,
        providerProfile,
        setProfile,
        setCustomerProfile,
        setProviderProfile,
        setSession,
        logout,
        refreshMe,
        view,
        setView,
        availableViews,
        hasMultipleViews,
        isAdmin,
        isCustomer,
        isProvider,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

// Small helper for post-auth redirects — pure function, no React state.
// Mirrors the priority order used by pickDefaultView so screens that
// don't have AuthContext available (e.g. raw redirect paths) stay
// consistent with the dashboard the user actually lands on.
export function pathForView(view) {
  if (view === "admin") return "/admin";
  if (view === "provider") return "/dashboard/provider";
  return "/dashboard/customer";
}

export function pickPostLoginPath(user) {
  const v = pickDefaultView(user);
  return v ? pathForView(v) : "/";
}
